import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useMatch, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_BASE } from './config';
import { Login } from './components/Login';
import { Signup } from './components/Signup';
import { DeskSidebar } from './components/DeskSidebar';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';
import { PANEL_BODY_FILL_CLASSNAME, APP_PANEL_SCROLL_CLASSNAME } from './constants/layout';
import { useLocale } from './contexts/LocaleContext';
import { useToast } from './contexts/ToastContext';
import { useFileImport } from './hooks/useFileImport';
import { useFeatureFlag } from './hooks/use-feature-flag';
import { FEATURE_FLAG_WIZARD_IMPORT_BUTTON } from './constants/feature-flags';
import { WizardImportModal } from './components/WizardImportModal';
import { runImportAnalyses } from './utils/import-analyses';
import type { RunImportAnalysesParams } from './utils/import-analyses';
import { getDocumentPath } from './utils/document-panel-paths';
import { buildDocumentContentBody } from './utils/yjs-content-payload';
import { UsageAndBilling } from './components/UsageAndBilling';
import { SharedWithMe } from './components/SharedWithMe';
import { TierLimitModal } from './components/TierLimitModal';
import { UserNotes, type UserNotesHandle } from './components/UserNotes';
import { DeskHeader } from './components/DeskHeader';
import { DeskMobileNavHeader } from './components/DeskMobileNavHeader';
import { DeskHomePanel } from './components/DeskHomePanel';
import { isDeskMobileNavView } from './utils/desk-primary-nav';
import { DeskProjectActions } from './components/DeskProjectActions';
import { DeskNotesActions } from './components/DeskNotesActions';
import { DeskProjectList, type DeskTrashedNote } from './components/DeskProjectList';
import { DeskNotifications } from './components/DeskNotifications';
import { DeskNotificationsActions } from './components/DeskNotificationsActions';
import { useIsMobile } from './hooks/use-mobile';
import { trackFirstProjectCreated, trackProjectCreationSuccess, trackProjectCreationFailed, trackProjectImportStarted } from './utils/analytics';
import type { DeskProject } from './types/desk-project';
import { USER_NOTES_LIMIT } from './constants';
import { isDeskNotesPath, shouldOpenCreateNoteFromLocation } from './utils/desk-notes-location';
import { shouldShowHeaderCreateActions } from './utils/header-create-actions';
import { notifyUserNotesChanged } from './utils/user-notes-events';

type DeskView = 'home' | 'all' | 'trash' | 'settings' | 'notifications' | 'shared-with-me' | 'usage-and-billing' | 'notes';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const trashMatch = useMatch('/desk/trash');
  const settingsMatch = useMatch('/desk/settings');
  const notificationsMatch = useMatch('/desk/notifications');
  const sharedWithMeMatch = useMatch('/desk/shared-with-me');
  const usageAndBillingMatch = useMatch('/desk/usage-and-billing');
  const projectsMatch = useMatch({ path: '/projects', end: true });
  const isNotesView = isDeskNotesPath(location.pathname);
  const shouldOpenCreateNote = shouldOpenCreateNoteFromLocation(location.pathname, searchParams);
  const isImportRoute = location.pathname === '/desk/import';
  const { token, user, loading: authLoading } = useAuth();
  const wizardImportButtonEnabled = useFeatureFlag(FEATURE_FLAG_WIZARD_IMPORT_BUTTON);
  const { t } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState<DeskProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<import('./components/UserNotes').UserNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [trashedNotes, setTrashedNotes] = useState<DeskTrashedNote[]>([]);
  const [restoringNoteId, setRestoringNoteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [wizardImportOpen, setWizardImportOpen] = useState(false);
  const importRouteOpenAttemptedRef = useRef(false);
  const notesRef = useRef<UserNotesHandle>(null);
  const [showSignup, setShowSignup] = useState<boolean>(false);
  const view: DeskView = trashMatch
    ? 'trash'
    : settingsMatch
      ? 'settings'
      : notificationsMatch
        ? 'notifications'
        : sharedWithMeMatch
          ? 'shared-with-me'
          : usageAndBillingMatch
            ? 'usage-and-billing'
            : isNotesView
              ? 'notes'
              : projectsMatch || isImportRoute || location.pathname === '/documents'
                ? 'all'
                : 'home';
  const [tierLimitModalOpen, setTierLimitModalOpen] = React.useState(false);
  const [tierLimitType, setTierLimitType] = React.useState<'project' | 'document' | 'resource' | 'storage'>('project');

  const fetchProjects = async (options?: { silent?: boolean }): Promise<void> => {
    const silent = options?.silent ?? false;
    if (!token || view === 'home' || view === 'settings' || view === 'notifications' || view === 'shared-with-me' || view === 'usage-and-billing' || view === 'notes') {
      if (!silent) {
        setLoading(false);
      }
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      const trashed = view === 'trash';
      const url = `${API_BASE}/api/projects${trashed ? '?trashed=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(t('failed_to_load_documents'));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchUserNotes = async (): Promise<void> => {
    if (!token || view !== 'notes') {
      setNotesLoading(false);
      return;
    }
    try {
      setNotesLoading(true);
      const response = await fetch(`${API_BASE}/api/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch notes');
      const data = await response.json();
      setNotes(data.notes || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const fetchTrashedNotes = async (): Promise<void> => {
    if (!token || view !== 'trash') {
      setTrashedNotes([]);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/notes?trashed=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch trashed notes');
      const data = await response.json();
      setTrashedNotes(
        (data.notes || []).map((note: { id: string; content: string; updatedAt: string }) => ({
          id: note.id,
          content: note.content,
          updatedAt: note.updatedAt,
        }))
      );
    } catch (err) {
      console.error('Error fetching trashed notes:', err);
      setTrashedNotes([]);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchUserNotes();
    fetchTrashedNotes();
  }, [token, view]);

  useEffect(() => {
    const handleProjectLimitReached = (): void => {
      setTierLimitType('project');
      setTierLimitModalOpen(true);
    };

    const handleStorageLimitReached = (): void => {
      setTierLimitType('storage');
      setTierLimitModalOpen(true);
    };

    window.addEventListener('project-limit-reached', handleProjectLimitReached);
    window.addEventListener('storage-limit-reached', handleStorageLimitReached);

    return () => {
      window.removeEventListener('project-limit-reached', handleProjectLimitReached);
      window.removeEventListener('storage-limit-reached', handleStorageLimitReached);
    };
  }, []);

  const handleCreateDocument = async (): Promise<void> => {
    if (!token || isCreatingDocument) {
      return;
    }

    setIsCreatingDocument(true);

    try {
      const response = await fetch(`${API_BASE}/api/projects`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: t('new_project_title'),
          documentTitle: t('new_document_title'),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'PROJECT_LIMIT_REACHED') {
          trackProjectCreationFailed('project_limit_reached');
          setTierLimitType('project');
          setTierLimitModalOpen(true);
          return;
        }
        throw new Error('Failed to create document');
      }

      const data = await response.json();
      trackProjectCreationSuccess();
      if (projects.length <= 1 && user) {
        trackFirstProjectCreated(user.id);
      }
      navigate(`/projects/${data.projectId}/documents/${data.id}`);
    } catch (err) {
      trackProjectCreationFailed('unknown');
      console.error('Error creating document:', err);
      setError(t('failed_to_create_document'));
    } finally {
      setIsCreatingDocument(false);
    }
  };

  const { handleImportFile, importFile, isImportingFile } = useFileImport({
    token: token || '',
    t,
    showToast,
    createDocument: async (title: string, content?: Uint8Array) => {
      const body: { title: string; contentBase64?: string } = { title };
      if (content) {
        Object.assign(body, buildDocumentContentBody(content));
      }

      const response = await fetch(`${API_BASE}/api/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'PROJECT_LIMIT_REACHED') {
          setTierLimitType('project');
          setTierLimitModalOpen(true);
          throw new Error('PROJECT_LIMIT_REACHED');
        }
        if (response.status === 413 && errorData.errorCode === 'IMPORT_CONTENT_TOO_LARGE') {
          throw new Error('IMPORT_CONTENT_TOO_LARGE');
        }
        throw new Error('Failed to create document');
      }

      return await response.json();
    },
    onSuccess: async () => {
      if (projects.length <= 1 && user) {
        trackFirstProjectCreated(user.id);
      }
      await fetchProjects({ silent: true });
    },
  });

  const tryOpenWizardImport = useCallback(async (): Promise<boolean> => {
    if (!token || !wizardImportButtonEnabled) {
      return false;
    }
    try {
      const response = await fetch(`${API_BASE}/api/settings/tier-limits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.maxProjects !== null && data.currentProjectCount >= data.maxProjects) {
          setTierLimitType('project');
          setTierLimitModalOpen(true);
          return false;
        }
      }
    } catch {
    }
    setWizardImportOpen(true);
    return true;
  }, [token, wizardImportButtonEnabled]);

  useEffect(() => {
    if (!isImportRoute) {
      importRouteOpenAttemptedRef.current = false;
      return;
    }
    if (authLoading || !token || !user || importRouteOpenAttemptedRef.current) {
      return;
    }
    importRouteOpenAttemptedRef.current = true;

    void (async () => {
      const opened = await tryOpenWizardImport();
      if (opened) {
        trackProjectImportStarted();
      } else {
        navigate('/projects', { replace: true });
      }
    })();
  }, [isImportRoute, authLoading, token, user, tryOpenWizardImport, navigate]);

  const handleWizardImportClose = (): void => {
    setWizardImportOpen(false);
    if (isImportRoute) {
      navigate('/projects', { replace: true });
    }
  };

  const handleWizardImportFinish = async ({
    documentId,
    projectId,
    suggestionsText,
    selection,
  }: RunImportAnalysesParams): Promise<void> => {
    let hasSuggestions = false;
    if (token) {
      const result = await runImportAnalyses(token, { documentId, projectId, suggestionsText, selection });
      hasSuggestions = result.hasSuggestions;
    }
    setWizardImportOpen(false);
    const documentPath = getDocumentPath(projectId ?? undefined, documentId);
    if (hasSuggestions) {
      navigate(`${documentPath}?openSuggestions=1`);
    } else {
      navigate(documentPath);
    }
  };

  const handleWizardImportClick = (): void => {
    void tryOpenWizardImport();
  };

  const handleDeleteProject = async (projectId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete project');
      }

      await fetchProjects();
      showToast(t('project_moved_to_trash'));
    } catch (err) {
      console.error('Error deleting project:', err);
      showErrorToast(t('failed_to_delete_project') || 'Failed to delete project');
    }
  };

  const handleRestoreProject = async (projectId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/restore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore project');
      }

      await fetchProjects();
      showToast(t('project_restored') || 'Project restored');
    } catch (err) {
      console.error('Error restoring project:', err);
      showErrorToast(t('failed_to_restore_project') || 'Failed to restore project');
    }
  };

  const handleRestoreNote = async (noteId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setRestoringNoteId(noteId);
      const response = await fetch(`${API_BASE}/api/notes/${noteId}/restore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore note');
      }

      setTrashedNotes((prev) => prev.filter((note) => note.id !== noteId));
      notifyUserNotesChanged();
      showToast(t('note_restored') || 'Note restored');
    } catch (err) {
      console.error('Error restoring note:', err);
      showErrorToast(t('failed_to_restore_note') || 'Failed to restore note');
    } finally {
      setRestoringNoteId(null);
    }
  };

  if (authLoading) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div>{t('loading')}</div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
        {showSignup ? (
          <Signup onSwitchToLogin={() => setShowSignup(false)} />
        ) : (
          <Login onSwitchToSignup={() => setShowSignup(true)} />
        )}
      </div>
    );
  }

  if (loading && view !== 'home' && view !== 'settings' && view !== 'notifications' && view !== 'shared-with-me' && view !== 'notes') {
    return (
      <>
        <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div>{t('loading')}</div>
        </div>
        <WizardImportModal
          isOpen={wizardImportOpen}
          onClose={handleWizardImportClose}
          onImport={importFile}
          onFinish={handleWizardImportFinish}
          isImporting={isImportingFile}
        />
      </>
    );
  }

  const projectActionProps = {
    disabled: !token,
    isCreating: isCreatingDocument,
    isImporting: isImportingFile,
    onCreate: handleCreateDocument,
    onImportFile: handleImportFile,
    onWizardImport: handleWizardImportClick,
  };
  const deskActions = ((): React.ReactNode => {
    if (view === 'all' && shouldShowHeaderCreateActions(projects.length > 0, isMobile)) {
      return <DeskProjectActions {...projectActionProps} />;
    }
    if (view === 'notes' && notes.length < USER_NOTES_LIMIT && shouldShowHeaderCreateActions(notes.length > 0, isMobile)) {
      return (
        <DeskNotesActions
          disabled={!token}
          onCreate={() => notesRef.current?.openCreate()}
        />
      );
    }
    if (view === 'notifications') {
      return <DeskNotificationsActions />;
    }
    return undefined;
  })();

  return (
    <SidebarProvider>
      <DeskSidebar />
      <SidebarInset style={{ overflowX: 'hidden', maxWidth: '100%' }}>
        {isMobile && isDeskMobileNavView(view) ? (
          <DeskMobileNavHeader actions={deskActions} />
        ) : (
          <DeskHeader actions={deskActions} />
        )}
        <div
          data-app-scroll-region={view === 'notes' || view === 'all' || view === 'trash' ? true : undefined}
          className={view === 'notes' || view === 'all' || view === 'trash' ? `${PANEL_BODY_FILL_CLASSNAME} ${APP_PANEL_SCROLL_CLASSNAME}` : undefined}
          style={{ padding: isMobile ? '1rem' : '2rem', width: '100%', maxWidth: '100%', height: view === 'notes' || view === 'all' || view === 'trash' ? undefined : '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          {view === 'home' ? (
            <DeskHomePanel />
          ) : view === 'settings' ? (
            <Outlet />
          ) : view === 'shared-with-me' ? (
            <SharedWithMe />
          ) : view === 'notifications' ? (
            <DeskNotifications />
          ) : view === 'usage-and-billing' ? (
            <UsageAndBilling />
          ) : view === 'notes' ? (
            <UserNotes
              ref={notesRef}
              token={token}
              notes={notes}
              loading={notesLoading}
              onRefresh={fetchUserNotes}
              initialOpenCreate={shouldOpenCreateNote}
            />
          ) : (
            <DeskProjectList
              projects={projects}
              isTrash={view === 'trash'}
              error={error}
              onDelete={handleDeleteProject}
              onRestore={handleRestoreProject}
              trashedNotes={view === 'trash' ? trashedNotes : []}
              onRestoreNote={view === 'trash' ? handleRestoreNote : undefined}
              restoringNoteId={restoringNoteId}
              emptyStateActions={<DeskProjectActions {...projectActionProps} variant="empty" />}
            />
          )}
        </div>
      </SidebarInset>
      <TierLimitModal open={tierLimitModalOpen} onOpenChange={setTierLimitModalOpen} limitType={tierLimitType} />
      <WizardImportModal
        isOpen={wizardImportOpen}
        onClose={handleWizardImportClose}
        onImport={importFile}
        onFinish={handleWizardImportFinish}
        isImporting={isImportingFile}
      />
    </SidebarProvider>
  );
};

export default App;
